// Base model for supporting validation.
// Implements interface to third-party validation tools, and provides standard "validate" method.
// Extend this for other models.
// This module provides:
//    constructor - saves data as properties for keys matching the schema keys
//    validate(callback) - validate the property data against the schema & return results
//    static validate(data, callback) - Combines constructing an object & running validation on the data
//    toObject()  - Returns the object properties as a simple Object

const {validations, validateAll} = require('indicative/validator')

// Map of our "type" to Indicative Validations
const types = {
   alphanumeric: 'alpha_numeric',
   checkbox: 'accepted',
   // Alphanumerics, and a few allowed symbols. This could be smarter...
   name: validations.regex([/^[a-z\s\-']+$/i])
}

// Map of schema options/parameters to Indicative validator methods. Used to provide alternate
// names and for backward compatibility
const options = {
   'required_when': 'requiredWhen',
   'format': 'regex'
}

class Validate {
   /**
    * Create a Validation object, passing in the data to test.
    * This can be called as a sub-class, where the parent class defines schema and messages,
    * or stand-alone by optionally passing in schema and messages.
    * @param {*} data - Object of fields and their values
    * @param {*} schema - Definition of expected fields schema
    * @param {*} messages - Custom error messages for when a field schema rule fails
    */
   constructor(data, schema, messages) {
      // Set schema before we set data, as it only copies defined fields
      if (schema)
         this.schema = schema
      if (messages)
         this.messages = messages
      this.data = data
   }

   get schema() {
      return this._schema || { }
   }

   set schema(value) {
      this._schema = value
   }

   get messages() {
      return this._messages || { }
   }

   set messages(value) {
      this._messages = value
   }

   set data(data) {
      // Use our schema keys to map data properties to object
      if (data)
         Object.keys(this.schema).forEach(p => this[p] = data[p])
      // Ensure there's no "last validate" data
      delete this._last_validate
   }

   get _indicative_rules() {
      // Convert our schema into required format for validate.js
      let schema = this.schema
      let indrules = {}
      Object.keys(schema).forEach(field => {
         const field_schema = schema[field] || {}
         let rules = []

         Object.keys(field_schema).forEach(r => {
            let rule = null
            switch (r) {
               case 'type':
                  // Convert our "type" into a Validation
                  rule = types[field_schema.type] || (validations[field_schema.type] && [validations[field_schema.type]()])
                  if (!rule)
                     console.warn(`Unrecognised schema type '${field_schema.type}' for field '${field}'`)
                  break
               default:
                  // Additional validation requirements and their options
                  rule = options[r] || r
                  // console.log(`Calling: validations.${rule}([${field_schema[r]}])`)
                  let args = field_schema[r]
                  // Rule args are expected to always be an array, but might already be...
                  rule = validations[rule](Array.isArray(args) ? args : [args]) || null
                  if (!rule)
                     console.warn(`Unrecognised schema rule '${r}' for field '${field}'`)
            }
            if (rule)
               rules.push(...(Array.isArray(rule) ? rule : [rule]))
         })

         indrules[field] = rules
      })
      // console.log('Schema => Indicative', {schema, indrules})
      return indrules
   }

   get _indicative_messages() {
      let indmsgs = Object.assign({}, Validate._default_messages);
      if (!this.messages)
         return indmsgs
      Object.keys(this.messages).forEach(key => {
         let [all, field, rule] = key.match(/^(.*)\.([^\.]+)$/)||[]
         let msg = this.messages[key]
         if (all && rule) {
            if (options[rule])
               rule = options[rule]
            indmsgs[field+'.'+rule] = msg
         } else {
            indmsgs[key] = msg
         }
      })
      // console.log('Validation Messages', this.messages, indmsgs)
      return indmsgs
   }

   /**
    * Used by plugins to set default type messages, as this is no longer part of the
    * plugin validation response.
    * @param {string} type
    * @param {string} message
    */
   static setDefaultMessage(type, message) {
      if (!this._default_messages)
         this._default_messages = {}
         this._default_messages[type] = message
   }

   async validate(callback) {
      try {
         // console.log('Validating!', this.toObject(), this._indicative_rules)
         await validateAll(this, this._indicative_rules, this._indicative_messages)
         this._last_validate = null // Property exists but is null
      } catch (errors) {
         if (errors instanceof Error) throw errors
         // Convert from array of errors to keyed by field name
         this._last_validate = {}
         errors.forEach(err => {
            if (err.field in this._last_validate)
               this._last_validate[err.field].push(err.message)
            else
               this._last_validate[err.field] = [err.message]
         })
      }
      // console.log('Validated', this, this._indicative_rules, this._last_validate)
      return callback ? callback(null, this._last_validate) : this._last_validate
   }

   get isValid() {
      if (!('_last_validate' in this))
         throw(new Error('Calling await o.validate() first is now required'))

      return this._last_validate ? false : true
   }

   get isInvalid() {
      return !this.isValid
   }

   get errors() {
      if (!('_last_validate' in this))
         throw(new Error('Calling await o.validate() first is now required'))
      return this._last_validate
   }

   validateSummary() {
      if (this.isValid) // This also inits validation if necessary
         return 'Ok'

      return Object.keys(this._last_validate).map(key => this._last_validate[key].join(', ')).join(', ')
      // return this._last_validate.map(v => v.message).join(', ')
   }

   // Static validate, to allow simple validation of form data without keeping the object around
   static async validate(data, callback) {
      return await (new this(data)).validate(callback)
   }

   // Convert properties to normal data object
   toObject() {
      return Object.assign({}, this)
   }

}

module.exports = Validate
